import React, { ReactElement, useContext } from "react"
import { Box } from "@mui/material"
import TableHeadCell from "./TableHeadCell"
import TableStyleContext from "../TableStyleContext"
import VirtualTableContext from "../VirtualTableContext"
import { Column, SortTypes } from "../types"
import { TableHeaderCheckboxCell } from "./TableHeaderCheckboxCell"
import { TableHeaderActionsCell } from "./TableHeaderActionsCell"
import TableCell from "./TableCell"
import { DropdownMenuOptions } from "../../../../mp-shared-components/generic-shared-components/components"

interface Props {
  headerColumns: Column[]
  selected: boolean
  sortBy: string
  order: SortTypes
  displayedProperties: string[]
  selectable?: boolean
  onSort: (sortBy: string, order: SortTypes) => void
  getRowAction?: (
    id: string,
    record?: { [key: string]: string }
  ) => ReactElement
  onSelectAllRows?: (selectAll: boolean) => void
  dropdownOptions: DropdownMenuOptions[]
  hideHeaderSelection?: boolean
  hasDiagonalHeaders?: boolean
  stickyFirstColumn?: boolean
}

const TableHeadComponent: React.FC<Props> = ({
  headerColumns,
  sortBy: currentSortBy,
  order: currentOrder,
  selected = false,
  displayedProperties,
  selectable = false,
  hasDiagonalHeaders = false,
  stickyFirstColumn = false,
  onSort,
  getRowAction,
  onSelectAllRows,
  dropdownOptions,
  hideHeaderSelection = false
}) => {
  const { cx, classes } = useContext(TableStyleContext)
  const { table } = useContext(VirtualTableContext)

  if (!table.getVisibleFlatColumns().length) {
    return <></>
  }

  return (
    <Box role="rowgroup" className={classes.head}>
      {table.getHeaderGroups().map(headerGroup => {
        const hasSubheaders = headerGroup.headers.some(
          header => header.subHeaders.length > 0
        )
        const isGrouped = hasSubheaders || headerGroup.depth > 0

        return (
          <Box
            role="row"
            key={headerGroup.id}
            className={cx(classes.headRow, {
              [classes.headRowGroup]: hasSubheaders
            })}
            data-automation-id="data-table-head-row"
          >
            {selectable && (
              <>
                {/* Checkbox or placeholder. */}
                {!hasSubheaders ? (
                  hideHeaderSelection ? (
                    <TableCell
                      role="columnheader"
                      className={cx(
                        classes.headTableCell,
                        classes.checkboxCell
                      )}
                    />
                  ) : (
                    <TableHeaderCheckboxCell
                      selected={selected}
                      onSelectAllRows={onSelectAllRows}
                    />
                  )
                ) : (
                  <TableCell
                    role="columnheader"
                    className={cx(classes.headTableCell, classes.checkboxCell)}
                  />
                )}
              </>
            )}

            {headerGroup.headers.map((header, i, arr) => {
              const isSameGroup =
                header.column.parent &&
                header.column.parent?.id === arr[i + 1]?.column?.parent?.id
              const classNames = {
                [classes.groupedCell]: isGrouped,
                [classes.groupCell]: isSameGroup,
                [classes.firstColumn]: stickyFirstColumn && i === 0
              }
              const style = {
                width: header.column.getSize(),
                flex: header.column.getCanResize() ? undefined : "none"
              }

              if (header.isPlaceholder) {
                return (
                  <TableCell
                    role="columnheader"
                    key={header.column.columnDef.id}
                    className={cx(classes.headTableCell, classNames)}
                    colSpan={header.colSpan}
                    rowSpan={header.rowSpan}
                    style={style}
                  />
                )
              }

              const columnDef = header.column.columnDef as any
              const diagonalHeader = columnDef.diagonalHeader || false

              const isFirstDiagonalHeader =
                diagonalHeader &&
                !headerGroup.headers
                  .slice(0, i)
                  .some(h => (h.column.columnDef as any).diagonalHeader)

              return (
                <TableHeadCell
                  key={header.id}
                  name={header.column.columnDef.id}
                  sortableName={columnDef.sortableName}
                  label={header.column.columnDef.header}
                  tooltip={(header.column.columnDef.meta as any)?.tooltip}
                  sortable={header.column.getCanSort()}
                  sortBy={currentSortBy}
                  order={currentOrder}
                  onSort={onSort}
                  colSpan={header.colSpan}
                  rowSpan={header.rowSpan}
                  diagonalHeader={diagonalHeader}
                  isFirstDiagonalHeader={isFirstDiagonalHeader}
                  hasDiagonalHeaders={hasDiagonalHeaders}
                  className={cx(
                    classNames,
                    classes[`align_${header.column.columnDef.meta["align"]}`]
                  )}
                  style={style}
                />
              )
            })}

            {(!!getRowAction || !!displayedProperties) && (
              <>
                {/* Spacer cell before action cell - only for diagonal headers. */}
                {hasDiagonalHeaders && (
                  <TableCell
                    role="columnheader"
                    className={cx(
                      classes.tableCell,
                      classes.spacerCell,
                      "header-cell"
                    )}
                  />
                )}
                {/* Action or placeholder. */}
                {!hasSubheaders ? (
                  <TableHeaderActionsCell
                    displayedProperties={displayedProperties}
                    dropdownOptions={dropdownOptions}
                    hasDiagonalHeaders={hasDiagonalHeaders}
                  />
                ) : (
                  <TableCell
                    role="columnheader"
                    className={cx(classes.headTableCell, classes.actionCell)}
                  />
                )}
              </>
            )}
          </Box>
        )
      })}
    </Box>
  )
}

export default React.memo(TableHeadComponent)
